import React, { useMemo, useRef, useState, useCallback } from 'react';
import { MapPin, Loader2, Layers, ExternalLink } from 'lucide-react';
import Map, { Layer, Source, Marker, NavigationControl, type MapRef } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { PortfolioSite } from '../../../redux/api/portfolioApi';
import { useGetSpatialLayersQuery } from '../../../redux/api/spatialApi';
import { API_BASE_URL } from '../../../config/apiConfig';
import { SessionManager } from '../../../redux/sessionManager';
import { getLayerStyle, getSortedLayerTypes } from '../../map/mapStyles';

interface SiteMapModuleProps {
  siteId: string;
  site: PortfolioSite;
  onOpenNodeView?: () => void;
  onOpenFullMap?: () => void;
}

/** Parse lat/lon from site metadata. Returns [lon, lat] for maplibre. */
function parseSiteCoords(site: any): [number, number] | null {
  const latLon = site?.meta?.site_lat_lon;
  if (latLon) {
    if (typeof latLon === 'string') {
      const parts = latLon.split(',').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return [parts[1], parts[0]];
      }
    }
    if (Array.isArray(latLon) && latLon.length === 2) {
      return [Number(latLon[1]), Number(latLon[0])];
    }
  }
  const location = site?.location;
  if (typeof location === 'string' && location.includes(',')) {
    const parts = location.split(',').map(Number);
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return [parts[1], parts[0]];
    }
  }
  return null;
}

/** Satellite base style for maplibre */
const SATELLITE_STYLE = {
  version: 8 as const,
  sources: {
    'esri-satellite': {
      type: 'raster' as const,
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: '&copy; Esri',
      maxzoom: 19,
    },
  },
  layers: [
    {
      id: 'satellite',
      type: 'raster' as const,
      source: 'esri-satellite',
      minzoom: 0,
      maxzoom: 22,
    },
  ],
};

const SiteMapModule: React.FC<SiteMapModuleProps> = ({ siteId, site, onOpenNodeView, onOpenFullMap }) => {
  const mapRef = useRef<MapRef>(null);
  const coords = useMemo(() => parseSiteCoords(site), [site]);
  const siteName = site.site_name || site.name || 'Site';

  const {
    data: layersData,
    isLoading,
  } = useGetSpatialLayersQuery(siteId, { skip: !siteId });

  const hasSpatialData = !!(layersData && layersData.layers && layersData.layers.length > 0 && layersData.bbox && layersData.center);

  // Fit map to bbox once loaded
  const handleMapLoad = useCallback(() => {
    if (hasSpatialData && layersData?.bbox) {
      const [minx, miny, maxx, maxy] = layersData.bbox as [number, number, number, number];
      mapRef.current?.fitBounds(
        [[minx, miny], [maxx, maxy]],
        { padding: 40, duration: 0 }
      );
    }
  }, [hasSpatialData, layersData]);

  // MVT tile URL with auth
  const tileUrl = useMemo(() => {
    const authHeaders = SessionManager.getAuthHeaders();
    const token = authHeaders['Authorization']?.replace('Bearer ', '') || '';
    return `${API_BASE_URL}/api/v1/spatial/sites/${siteId}/tiles/{z}/{x}/{y}.mvt?token=${token}`;
  }, [siteId]);

  // Layer visibility — default to showing inverter layer if available
  const availableLayers = useMemo(() => {
    if (!layersData) return [];
    return getSortedLayerTypes(layersData.layers.map(l => l.type));
  }, [layersData]);

  const [visibleLayers, setVisibleLayers] = useState<Set<string> | null>(null);

  // Initialize visible layers once data loads
  const effectiveVisibleLayers = useMemo(() => {
    if (visibleLayers) return visibleLayers;
    if (!availableLayers.length) return new Set<string>();
    // Default: show inverter_base if it exists, then any inverter, otherwise show all
    const inverterBase = availableLayers.find(l => l === 'inverter_base');
    if (inverterBase) return new Set([inverterBase]);
    const inverterLayer = availableLayers.find(l => l.toLowerCase().includes('inverter'));
    if (inverterLayer) return new Set([inverterLayer]);
    return new Set(availableLayers);
  }, [visibleLayers, availableLayers]);

  const handleToggleLayer = useCallback((layerType: string) => {
    setVisibleLayers(prev => {
      const current = prev || effectiveVisibleLayers;
      const next = new Set(current);
      if (next.has(layerType)) {
        next.delete(layerType);
      } else {
        next.add(layerType);
      }
      return next;
    });
  }, [effectiveVisibleLayers]);

  // Determine map center
  const mapCenter = useMemo((): [number, number] | null => {
    if (hasSpatialData && layersData.center) {
      return layersData.center as [number, number];
    }
    return coords;
  }, [hasSpatialData, layersData, coords]);

  if (isLoading) {
    return (
      <div className="h-96 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-bg-tertiary)' }}>
        <Loader2 className="w-5 h-5 animate-spin text-engramiq-green" />
      </div>
    );
  }

  if (!mapCenter) {
    return (
      <div className="h-96 rounded-lg flex flex-col items-center justify-center gap-2" style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
        <MapPin className="w-8 h-8 opacity-40" />
        <p className="text-xs">No location data for {siteName}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Map */}
      <div className="h-96 rounded-lg overflow-hidden relative">
        <Map
          ref={mapRef}
          mapLib={maplibregl as any}
          initialViewState={{
            longitude: mapCenter[0],
            latitude: mapCenter[1],
            zoom: hasSpatialData ? 15 : 14,
            pitch: 0,
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={SATELLITE_STYLE}
          onLoad={handleMapLoad}
          maxZoom={22}
          minZoom={4}
        >
          <NavigationControl position="top-left" showCompass={false} />

          {/* Spatial layers via MVT tiles */}
          {hasSpatialData && (
            <Source
              id="site-spatial"
              type="vector"
              tiles={[tileUrl]}
              minzoom={4}
              maxzoom={22}
            >
              {availableLayers
                .filter(layerType => effectiveVisibleLayers.has(layerType))
                .flatMap(layerType => {
                  const style = getLayerStyle(layerType);
                  const baseFilter: any[] = ['==', ['get', 'layer_type'], layerType];
                  if (style.type === 'fill') {
                    return [
                      <Layer
                        key={`fill-${layerType}`}
                        id={`site-fill-${layerType}`}
                        type="fill"
                        source-layer="spatial"
                        filter={baseFilter}
                        paint={{
                          'fill-color': style.color,
                          'fill-opacity': style.opacity,
                        }}
                      />,
                      <Layer
                        key={`outline-${layerType}`}
                        id={`site-outline-${layerType}`}
                        type="line"
                        source-layer="spatial"
                        filter={baseFilter}
                        paint={{
                          'line-color': style.outlineColor || style.color,
                          'line-opacity': Math.min(style.opacity + 0.2, 1),
                          'line-width': 1,
                        }}
                      />,
                    ];
                  }
                  return [
                    <Layer
                      key={`line-${layerType}`}
                      id={`site-line-${layerType}`}
                      type="line"
                      source-layer="spatial"
                      filter={baseFilter}
                      paint={{
                        'line-color': style.color,
                        'line-opacity': style.opacity,
                        'line-width': style.width || 2,
                      }}
                    />,
                  ];
                })}
            </Source>
          )}

          {/* Pin marker when no spatial data */}
          {!hasSpatialData && coords && (
            <Marker longitude={coords[0]} latitude={coords[1]} anchor="bottom">
              <div className="flex flex-col items-center">
                <div className="bg-engramiq-green text-engramiq-dark-blue text-[10px] px-1.5 py-0.5 rounded shadow-lg mb-0.5 whitespace-nowrap font-medium">
                  {siteName}
                </div>
                <MapPin size={24} className="text-engramiq-green drop-shadow-lg" fill="currentColor" />
              </div>
            </Marker>
          )}
        </Map>

        {/* Open full map button */}
        <button
          onClick={() => {
            if (onOpenFullMap) onOpenFullMap();
            else if (onOpenNodeView) onOpenNodeView();
          }}
          className="absolute top-2 right-2 p-1.5 rounded-md transition-colors z-10"
          style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-card)' }}
          title="Open full site map"
        >
          <ExternalLink className="w-3.5 h-3.5 text-engramiq-green" />
        </button>
      </div>

      {/* Layer toggles (only when spatial data exists) */}
      {hasSpatialData && availableLayers.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Layers className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
          {availableLayers.map(layerType => {
            const isActive = effectiveVisibleLayers.has(layerType);
            const style = getLayerStyle(layerType);
            const label = layerType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return (
              <button
                key={layerType}
                onClick={() => handleToggleLayer(layerType)}
                className="px-2 py-0.5 rounded text-[10px] font-medium transition-all"
                style={{
                  backgroundColor: isActive ? `${style.color}25` : 'transparent',
                  color: isActive ? style.color : 'var(--color-text-muted)',
                  border: `1px solid ${isActive ? `${style.color}40` : 'var(--color-border)'}`,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {!hasSpatialData && (
        <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
          No spatial layers &mdash; showing satellite pin
        </p>
      )}
    </div>
  );
};

export default SiteMapModule;
